import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';

// Hooks
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Selectors
import { getWorkspaceTeammate } from 'Shared/selectors/workspace';

// Actions
import { getWorkspacePrivateNoteAction } from 'Shared/actions/workspacePrivateNote';

// Components
import Spinner from 'Shared/components/Spinner';
import WorkspacePrivateZoneEditor from './WorkspacePrivateZoneEditor/WorkspacePrivateZoneEditor';
import SavingLoader from './SavingLoader/SavingLoader';

// Styles
import './WorkspacePrivateNotes.scss';

type Props = {
  isGroup: boolean;
  workspaceId: string;
};

function WorkspacePrivateNotes({ workspaceId, isGroup }: Props) {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { workspaceUuid } = useSelector(state => state.workspacePrivateNote);

  const teammate = useDeepEqualSelector(getWorkspaceTeammate(workspaceId));
  const { id: currentUserId } = useCurrentUser();

  useEffect(() => {
    async function fetchWorkspacePrivateNote() {
      if (workspaceUuid !== workspaceId) {
        await dispatch(getWorkspacePrivateNoteAction({ userId: currentUserId, workspaceId }));
      }
      setIsLoading(false);
    }
    fetchWorkspacePrivateNote();
  }, [currentUserId, dispatch, workspaceId, workspaceUuid]);

  const label = () => {
    if (isGroup) return `This is a place for you to take notes that you’re not ready for others to see yet (or ever).`;
    if (teammate) {
      return `This is a place for you to take notes that you’re not ready for ${
        teammate.firstName || teammate.email
      } to see yet (or ever).`;
    }
    return 'This is a place for your private notes';
  };

  return (
    <div className="WorkspacePrivateNotes d-flex flex-column position-relative pb-4">
      <div className={cn('WorkspacePrivateNotes__content  h-100')}>
        <div className="WorkspacePrivateNotes__title d-flex">
          Top Secret Zone
          {isSaving && <SavingLoader />}
        </div>
        <div className="WorkspacePrivateNotes__description mb-3">{label()}</div>
        {isLoading ? (
          <Spinner activeIf />
        ) : (
          <WorkspacePrivateZoneEditor workspaceId={workspaceId} onSetSaving={setIsSaving} />
        )}
      </div>
    </div>
  );
}

export default WorkspacePrivateNotes;
